/* assets/giddy-wishlist.js
   =========================================================================
   Wishlist persistence + drawer behaviour.
   - localStorage key: 'giddy:wishlist'
   - Each item is an object: { id, handle, title, image, url, price }
     (price is in cents, matches Shopify's cart.js conventions.)
   - Old format ("just an ID string") is migrated by being filtered out on
     read — small loss, but the format is structurally incompatible.

   Public API (window.GiddyWishlist):
     .get()                    → Array<item>
     .has(productId)           → bool
     .add(item) / .remove(id)  → mutate + dispatch event
     .toggle(item)             → bool (true = now in wishlist)
     .count()                  → number
     .openDrawer() / .closeDrawer()
     .render()                 → re-render drawer contents

   Events dispatched on document:
     giddy:wishlist:changed → detail: { list, count }
   ========================================================================= */
(function () {
  'use strict';

  var KEY = 'giddy:wishlist';

  function safeRead() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Migrate / clean: only keep items that have at least id + handle
      return parsed.filter(function (x) {
        return x && typeof x === 'object' && x.id && x.handle;
      });
    } catch (e) {
      return [];
    }
  }
  function safeWrite(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
  }
  function emit(list) {
    document.dispatchEvent(new CustomEvent('giddy:wishlist:changed', {
      detail: { list: list.slice(), count: list.length }
    }));
  }

  // Customer login state — set by Liquid via body[data-giddy-logged-in].
  // We re-read at call time so JS hot-paths reflect the latest body
  // attribute (e.g. if the user logs in via an in-page modal later).
  function isLoggedIn() {
    var body = document.body;
    if (!body) return false;
    return body.getAttribute('data-giddy-logged-in') === 'true';
  }

  // === Login-required popup ===
  // Shown when a logged-out customer tries to heart a product.
  // Styled to match the Giddy palette (cream card, Fraunces italic title,
  // peach Login button). Same content also appears inside the drawer
  // when a logged-out customer opens it.
  function showLoginPopup() {
    // Reuse an existing instance if already mounted
    var existing = document.getElementById('giddy-login-popup');
    if (existing) {
      existing.classList.add('is-open');
      document.body.classList.add('giddy-login-open');
      return;
    }
    var modal = document.createElement('div');
    modal.id = 'giddy-login-popup';
    modal.className = 'giddy-login-popup is-open';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML =
      '<div class="giddy-login-popup__backdrop" data-login-close></div>' +
      '<div class="giddy-login-popup__panel" role="document">' +
        '<button type="button" class="giddy-login-popup__close" data-login-close aria-label="Close">&times;</button>' +
        '<div class="giddy-login-popup__icon" aria-hidden="true">&#9825;</div>' +
        '<h3 class="giddy-login-popup__title">Login <em>required</em></h3>' +
        '<p class="giddy-login-popup__text">Log in to your account to add products to your wishlist and view your previously saved items.</p>' +
        '<div class="giddy-login-popup__actions">' +
          '<a href="/account/login" class="giddy-login-popup__btn giddy-login-popup__btn--primary">Login</a>' +
          '<button type="button" class="giddy-login-popup__btn giddy-login-popup__btn--ghost" data-login-close>Maybe later</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);
    document.body.classList.add('giddy-login-open');

    // Close handlers
    modal.querySelectorAll('[data-login-close]').forEach(function (el) {
      el.addEventListener('click', hideLoginPopup);
    });
  }
  function hideLoginPopup() {
    var modal = document.getElementById('giddy-login-popup');
    if (modal) modal.classList.remove('is-open');
    document.body.classList.remove('giddy-login-open');
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && document.body.classList.contains('giddy-login-open')) {
      hideLoginPopup();
    }
  });

  var Wishlist = {
    get: function () { return safeRead(); },

    isLoggedIn: isLoggedIn,
    showLoginPopup: showLoginPopup,
    hideLoginPopup: hideLoginPopup,

    has: function (productId) {
      if (productId == null) return false;
      var idStr = String(productId);
      return safeRead().some(function (x) { return String(x.id) === idStr; });
    },

    add: function (item) {
      if (!item || !item.id || !item.handle) return false;
      var list = safeRead();
      if (list.some(function (x) { return String(x.id) === String(item.id); })) {
        return false; // already there
      }
      list.push({
        id:     item.id,
        handle: item.handle,
        title:  item.title  || '',
        image:  item.image  || '',
        url:    item.url    || ('/products/' + item.handle),
        price:  (typeof item.price === 'number') ? item.price : null
      });
      safeWrite(list);
      emit(list);
      return true;
    },

    remove: function (productId) {
      var idStr = String(productId);
      var list = safeRead();
      var next = list.filter(function (x) { return String(x.id) !== idStr; });
      if (next.length === list.length) return false;
      safeWrite(next);
      emit(next);
      return true;
    },

    toggle: function (item) {
      // Logged-out customers can't save anything — they get the login
      // popup instead. Returning false signals to the calling card that
      // the heart's visual state should NOT flip on.
      if (!isLoggedIn()) {
        showLoginPopup();
        return false;
      }
      if (this.has(item.id)) { this.remove(item.id); return false; }
      return this.add(item);
    },

    count: function () { return safeRead().length; },

    // ── Drawer ──
    openDrawer: function () {
      var d = document.getElementById('giddy-wishlist-drawer');
      var o = document.getElementById('giddy-wishlist-overlay');
      if (!d || !o) return;

      // Coordinate with the cart drawer (if open) to avoid double-darken
      // and to skip the slide animation when this drawer is already open.
      var coord = (window.GiddyDrawers && window.GiddyDrawers.beforeOpen('wishlist')) || {};

      // Pin the drawer + overlay flush with the live nav bottom — same
      // pattern the cart drawer uses (see giddy-cart-drawer.js:openDrawer).
      // The static CSS var fallback was leaving a gap when the
      // announcement bar was visible because --giddy-header-height only
      // covers the header, not the announcement strip above it.
      var nav = document.querySelector('.header-wrapper');
      if (nav) {
        var navBottom = Math.max(0, Math.round(nav.getBoundingClientRect().bottom));
        d.style.top = navBottom + 'px';
        o.style.top = navBottom + 'px';
      }

      // Hide the cart drawer's overlay so we don't stack two dim layers.
      if (coord.hideOtherOverlay) {
        var otherOverlay = document.getElementById('cart-drawer-overlay');
        if (otherOverlay) { otherOverlay.style.opacity = '0'; otherOverlay.style.display = 'none'; }
      }

      this.render();
      // Clear any stale returning class from a previous close cycle
      document.body.classList.remove('giddy-wishlist-returning');
      o.style.display = 'block';
      requestAnimationFrame(function () { o.style.opacity = '1'; });
      // Skip slide animation when already open (coordinator returns
      // animate=false in that case — just a z-index bump to bring to front).
      if (coord.animate !== false) {
        d.style.transform = 'translateX(0)';
      }
      document.body.classList.add('giddy-wishlist-open');
    },
    closeDrawer: function () {
      var d = document.getElementById('giddy-wishlist-drawer');
      var o = document.getElementById('giddy-wishlist-overlay');
      if (!d || !o) return;
      d.style.transform = 'translateX(100%)';
      o.style.opacity = '0';
      document.body.classList.remove('giddy-wishlist-open');

      var coord = (window.GiddyDrawers && window.GiddyDrawers.afterClose('wishlist')) || {};

      if (coord.showOtherOverlay) {
        // Cart drawer is still open — bring its overlay back. Skip the
        // chat-bubble return since the cart's still holding the chat off.
        var otherOverlay = document.getElementById('cart-drawer-overlay');
        if (otherOverlay) {
          otherOverlay.style.display = 'block';
          requestAnimationFrame(function () { otherOverlay.style.opacity = '1'; });
        }
        setTimeout(function () { o.style.display = 'none'; }, 320);
      } else {
        // Nothing else open: full close, trigger chat-bubble return.
        setTimeout(function () { o.style.display = 'none'; }, 320);
        document.body.classList.add('giddy-wishlist-returning');
        setTimeout(function () {
          document.body.classList.remove('giddy-wishlist-returning');
        }, 750);
      }
    },
    toggleDrawer: function () {
      var d = document.getElementById('giddy-wishlist-drawer');
      if (d && d.style.transform === 'translateX(0)') this.closeDrawer();
      else this.openDrawer();
    },

    render: function () {
      var body = document.getElementById('giddy-wishlist-body');
      if (!body) return;

      // === Logged-out customers get a Login required state instead of
      // the items list / empty state. Same copy + look as the popup so
      // the experience is consistent whether they hit it from the heart
      // or by opening the drawer directly. ===
      if (!isLoggedIn()) {
        body.innerHTML =
          '<div class="giddy-wishlist__login">' +
            '<div class="giddy-wishlist__login-icon" aria-hidden="true">&#9825;</div>' +
            '<div class="giddy-wishlist__login-title">Login <em>required</em></div>' +
            '<p class="giddy-wishlist__login-text">Log in to your account to add products to your wishlist and view your previously saved items.</p>' +
            '<a href="/account/login" class="giddy-wishlist__shop-btn">Login</a>' +
          '</div>';
        return;
      }

      var list = safeRead();
      if (list.length === 0) {
        body.innerHTML =
          '<div class="giddy-wishlist__empty">' +
            '<div class="giddy-wishlist__empty-icon" aria-hidden="true">&#9825;</div>' +
            '<div class="giddy-wishlist__empty-title">Your wishlist is <em>empty</em></div>' +
            '<p class="giddy-wishlist__empty-text">Save your favourite cookies for later by tapping the heart on any product.</p>' +
            '<a href="/collections/cookies" class="giddy-wishlist__shop-btn">Shop Cookies</a>' +
          '</div>';
        return;
      }

      var html = '<ul class="giddy-wishlist__items" role="list">';
      list.forEach(function (item) {
        var priceHtml = '';
        if (typeof item.price === 'number') {
          // Shopify Liquid stores money_format; in JS we fall back to AUD.
          var dollars = (item.price / 100).toFixed(2);
          priceHtml = '<div class="giddy-wishlist__price">$' + dollars + '</div>';
        }
        var imgHtml = item.image
          ? '<img src="' + item.image + '" alt="' + (item.title || '') + '" loading="lazy">'
          : '<div class="giddy-wishlist__noimg" aria-hidden="true">&#9825;</div>';
        html += ''
          + '<li class="giddy-wishlist__item" data-pid="' + item.id + '">'
          +   '<a href="' + item.url + '" class="giddy-wishlist__thumb" aria-label="' + (item.title || '') + '">' + imgHtml + '</a>'
          +   '<div class="giddy-wishlist__details">'
          +     '<a href="' + item.url + '" class="giddy-wishlist__title">' + (item.title || 'Untitled') + '</a>'
          +     priceHtml
          +     '<div class="giddy-wishlist__actions">'
          +       '<a href="' + item.url + '" class="giddy-wishlist__view">View product</a>'
          +       '<button type="button" class="giddy-wishlist__remove" data-remove="' + item.id + '" aria-label="Remove from wishlist">Remove</button>'
          +     '</div>'
          +   '</div>'
          + '</li>';
      });
      html += '</ul>';
      html += '<div class="giddy-wishlist__footer">'
            +   '<a href="/collections/cookies" class="giddy-wishlist__shop-btn">Keep shopping</a>'
            + '</div>';
      body.innerHTML = html;
    }
  };

  // Wire up drawer interactions ONCE on first DOM-ready, then leave the
  // listeners running for the lifetime of the page.
  function bind() {
    var close = document.getElementById('giddy-wishlist-close');
    var overlay = document.getElementById('giddy-wishlist-overlay');
    if (close) close.addEventListener('click', function () { Wishlist.closeDrawer(); });
    if (overlay) overlay.addEventListener('click', function () { Wishlist.closeDrawer(); });

    // Remove button delegation
    var body = document.getElementById('giddy-wishlist-body');
    if (body) {
      body.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-remove]');
        if (!btn) return;
        Wishlist.remove(btn.dataset.remove);
        Wishlist.render();
      });
    }

    // ESC closes drawer
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('giddy-wishlist-open')) {
        Wishlist.closeDrawer();
      }
    });

    // === Header heart paint + shake sync ===
    //
    // syncShakeAnimations restarts the cart + wishlist shake animations
    // simultaneously, so when one of them gains items mid-page the two
    // icons immediately tick on the same 2.6s phase. CSS animations
    // attach when their class condition becomes true, so without this
    // re-trigger they'd be out of sync whenever one's state changed
    // after the other was already running.
    function syncShakeAnimations() {
      var els = [
        document.getElementById('cart-icon-bubble'),
        document.getElementById('giddy-wishlist-bubble')
      ].filter(Boolean);
      // Strip animation, force reflow, let CSS re-apply.
      els.forEach(function (el) {
        el.style.animation = 'none';
        // Force a reflow so the browser commits the animation: none
        // state before we restore the default. eslint-ignore-next-line.
        void el.offsetWidth;
      });
      // Restore default (CSS rule re-applies)
      els.forEach(function (el) { el.style.animation = ''; });
      // The bubbles too, so their pulse animation aligns
      document.querySelectorAll('.cart-count-bubble').forEach(function (b) {
        b.style.animation = 'none';
        void b.offsetWidth;
        b.style.animation = '';
      });
    }

    // Repaint header heart count + body class whenever the wishlist changes
    document.addEventListener('giddy:wishlist:changed', function (e) {
      var count = (e.detail && e.detail.count) || 0;
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count;
        // Defensive: mark with data-zero so the badge stays hidden even
        // if some other code mistakenly applies has-items at count = 0.
        el.setAttribute('data-zero', count === 0 ? 'true' : 'false');
      });
      document.querySelectorAll('[data-wishlist-trigger]').forEach(function (btn) {
        btn.classList.toggle('has-items', count > 0);
        btn.setAttribute('aria-label',
          count > 0 ? ('Wishlist (' + count + ' items)') : 'Wishlist (empty)');
      });
      // Body class drives the CSS — outline → filled, bubble visibility,
      // and shake animation. Mirrors the cart's body.giddy-cart-has-items.
      document.body.classList.toggle('giddy-wishlist-has-items', count > 0);

      // Re-sync both icons' shake animations whenever wishlist state changes
      syncShakeAnimations();

      // Re-render drawer if open so removed items disappear instantly
      if (document.body.classList.contains('giddy-wishlist-open')) Wishlist.render();
    });

    // Also sync when CART state changes (the cart toggle event is
    // dispatched by giddy-header-state.js / giddy-cart-drawer.js).
    document.addEventListener('giddy:cart:updated', syncShakeAnimations);

    // Paint initial header state
    document.dispatchEvent(new CustomEvent('giddy:wishlist:changed', {
      detail: { list: safeRead(), count: safeRead().length }
    }));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }

  window.GiddyWishlist = Wishlist;
})();
